import Backbone from 'backbone'
import _ from 'lodash'
import textLength from 'scoreboard/components/text-length'
import template from './slider.hbs'
import $ from 'jquery'
import isRtl from 'scoreboard/components/is-rtl'

/**
 * Usage: new Slider({input: myInputElement})
 * Creates a slider which will take up all the available space and act as a range
 * control. By default this slider will be blue and movable.
 * To create a non-movable slider, add `disabled="disabled"` to the input element.
 *
 * If you need to show the entire value (not a rounded value) in the handle,
 * pass in `roundedValue: false`
 *
 * If there should be a unit next to the number value (like 234 GB instead of just 234)
 ** pass it in like `unit: 'GB'`
 */
var Slider = Backbone.View.extend({
	className: 'slider',

	events: {
		'mousedown .thumb': 'moveStart',
		'touchstart .thumb': 'moveStart',
		'click .thumb': 'stopPropagation',
		'mousedown .bar-frame': 'barClick',
		'touchstart .bar-frame': 'barClick'
	},

	initialize: function(options) {
		this.input = $(options.input)
		this.roundedValue = _.isUndefined(options.roundedValue) ? true : this.roundedValue
		this.unit = options.unit
		this.isRtl = isRtl()

		if(this.input.prop('disabled')) {
			this.$el.addClass('slider-immobile')
		}

		_.bindAll(this, 'drag', 'drop')
	},

	render: function() {
		this.$el.html(template()).addClass(this.input.attr('class'))
		this.step = parseFloat(this.input.attr('step')) || 1
		this.input.attr('type', 'hidden')
		this.input.after(this.el)


		this.$('.min').html(this.getMin())
		this.$('.max').html(this.getMax())

		process.nextTick(function() {
			// we need to do this on the text tick to pick up padding values
			// assigned to the thumb by the browser. They we'll set the value
			// and show the thumb.
			this.thumbPadding = this.$('.thumb')[0].clientWidth / 2
			this.setValue(this.input.val())
			this.$el.addClass('is-ready')
		}.bind(this))

		return this
	},

	/*
	 * sets the positions of the thumb and the colored section of the bar based
	 * on the numeric value of the input element.
	 */
	setPositions: function(value) {
		if(value > this.getMax()) {
			value = this.getMax()
		}
		if(value < this.getMin()) {
			value = this.getMin()
		}
		this.positionOverbar(value)
		this.positionThumb(value)
	},

	/*
	 * Where events go to be ignored.
	 */
	stopPropagation: function(e) {
		e.stopPropagation()
	},

	/*
	 * called when the thumb starts to move. Sets initial conditions and makes
	 * the larger document not selectable.
	 */
	moveStart: function(e) {
		e.stopPropagation()
		if(!this.isMovable()) {
			return
		}
		this.mouseStart = e.pageX || e.originalEvent.touches[0].pageX
		this.startingValue = this.getValue()
		$(document).on('mousemove touchmove', this.drag)
		$(document).on('mouseup touchend', this.drop)
		$('body').addClass('is-not-selectable')

		// This makes sure that as we slide the thumb to a smaller number
		// that the pointer doesn't leave the thumb. Other ways of trying to
		// compensate for the changing thumb size were unreliable and looked bad.
		this.$('.thumb').css('min-width', this.$('.thumb').outerWidth())
		this.$el.addClass('is-selected')
	},

	/*
	 * Handles the case where a bar is clicked or touched.
	 */
	barClick: function(e) {
		if(!this.isMovable()) {
			return
		}
		var underBar = this.$('.under-bar')
		var pos = (e.pageX || e.originalEvent.touches[0].pageX) - underBar[0].getBoundingClientRect().left
		var width = underBar.width()
		portion = pos / width
		var value = this.calcValueFromPortion(portion)

		// we have do a secondary assessment so we can figure out what the thumb
		// roughly centered at this position would be.
		pos += this.getThumbSize(value) * (portion - .5)
		portion = pos / width
		value = this.calcValueFromPortion(portion)
		this.setValue(value)
	},

	/*
	 * Figure out which step our value corresponds to.
	 */
	makeSteppedDecimal: function(value) {
		value = Math.round(value / this.step) * this.step
		if(this.step < 1) {
			value = value.toFixed(Math.floor(Math.log10(this.step)) * -1)
		}
		return value
	},

	/*
	 * Handles mouse movement while dragging.
	 */
	drag: function(e) {
		e.stopPropagation()
		var mouseNow = (e.pageX || e.originalEvent.touches[0].pageX)
		var diff = mouseNow - this.mouseStart
		var portion = diff / this.getMaxThumbLeft()
		var value = this.startingValue
		var change = portion * (this.getMax() - this.getMin())
		value += change
		this.setValue(value)
	},

	/*
	 * Terminates the thumb drag, sets the final value and cleans up.
	 */
	drop: function(e) {
		e.stopPropagation()
		$(document).off('mousemove touchmove', this.drag)
		$(document).off('mouseup touchend', this.drop)
		$('body').removeClass('is-not-selectable')
		this.$('.thumb').css('min-width', '')
		this.$el.removeClass('is-selected')
	},

	/*
	 * Gets the offset from the container for the left edge of the thumb if the
	 * thumb is against the extreme right of the bar and is displaying the maximum
	 * value.
	 */
	getMaxThumbLeft: function() {
		var maxThumb = this.getThumbSize(this.getMax())
		return this.getBarWidth() - maxThumb
	},

	/*
	 * Get the width of the light gray positional bar.
	 */
	getBarWidth: function() {
		return this.$('.under-bar').width()
	},

	/*
	 * Gets the size of the thumb given a specific numeric value
	 */
	getThumbSize: function(value /* a numeric value */) {
		var thumbTextSize = this.getThumbTextSize(this.makeSteppedDecimal(value))
		var thumbSize = (2 * this.thumbPadding) + thumbTextSize
		return thumbSize
	},

	/*
	 * Gets the width of a given piece of text within the thumb.
	 */
	getThumbTextSize: function(value) {
		return textLength(this.$('.thumb'), value)
	},

	/*
	 * Given a portion of the range covered, what value is it.
	 */
	calcValueFromPortion: function(portion) {
		return ((this.getMax() - this.getMin()) * portion) + this.getMin()
	},

	/*
	 * Positions the colored/darker bar that shows the the left of the thumb.
	 */
	positionOverbar: function(value /* the current value of field */) {
		var portion = (value - this.getMin()) / (this.getMax() - this.getMin())
		const widthPercent = portion * 100;

		const overbarCss = this.isRtl ? {
			'right': 0,
			'left': 'auto',
			'width': widthPercent + '%'
		} : {
			'left': 0,
			'width': widthPercent + '%'
		}
		this.$('.over-bar').css(overbarCss)
	},

	/*
	 * Sets the value of the input control and the positions of the thumb and
	 * bars.
	 */
	setValue: function(value) {
		if(value > this.getMax()) {
			value = this.getMax()
		}
		if(value < this.getMin()) {
			value = this.getMin()
		}

		if(this.roundedValue) {
			value = this.makeSteppedDecimal(value)
		}
		this.setPositions(value)
		this.input.val(value)

		// `toLocaleString` adds commas to the displayed value
		this.$('.thumb').html(value.toLocaleString() + (this.unit ? ' ' + this.unit: '' ))
	},

	/*
	 * Gets the value of the input elements.
	 */
	getValue: function() {
		return parseFloat(this.input.val())
	},

	/*
	 * True if the input control is disabled.
	 */
	isMovable: function() {
		return !this.$el.hasClass('slider-immobile')
	},

	/*
	 * Positions the thumb according to the numeric value of the input control.
	 */
	positionThumb: function(value /* the current value of field */) {
		var portion = (value - this.getMin()) / (this.getMax() - this.getMin())
		const positionPercent = portion * 100
		const thumbCss = this.isRtl ? {
			'right': positionPercent + '%',
			'transform': 'translateX(' + positionPercent + '%)'
		} : {
			'left': positionPercent + '%',
			'transform': 'translateX(-' + positionPercent + '%)'
		}
		this.$('.thumb').css(thumbCss)
	},

	/*
	 * Gets the minimum value of the input control.
	 */
	getMin: function() {
		return parseFloat(this.input.attr('min'))
	},

	/*
	 * Gets the maximum value of the input control.
	 */
	getMax: function() {
		return parseFloat(this.input.attr('max'))
	}
})

export default Slider

Slider.__filename__ = 'components/slider.js'
